/**
 * F09 v2 — `/verifications/entities/:type/:id` entity-centric verification page.
 *
 * Shows the current tick (+ since), the live request, open requests, request
 * history, the watchlist and candidate rows, and the events timeline.
 * Actions: Revoke (category + reason ≥10 + type-to-confirm), Lift cooldown,
 * Unlock re-request (reason ≥10) — `verifications:review` — and Direct verify
 * (super_admin, `verifications:issue_privileged`).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useFormat, useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Spinner,
  VerificationBadge,
  VerifiedTick,
  useToast,
} from '@bravemove/ui-web';
import {
  VERIFIABLE_ENTITY_TYPES,
  type AdminEntityVerificationProfile,
  type AdminVerificationQueueItem,
  type CandidateReasonCode,
  type VerifiableEntityType,
  type WatchlistReasonCode,
} from '@bravemove/types';
import { adminVerificationApi } from '../../../../api/verification';
import { PageHeader } from '../../../../components/PageHeader';
import { useVerificationPermissions } from '../../../../hooks/useVerificationPermissions';
import { ReasonDialog, RevokeDialog } from '../../../../components/verification/DecisionDialogs';
import {
  EventsTimeline,
  Panel,
  RequestHistoryTable,
} from '../../../../components/verification/DetailPanels';
import {
  candidateReasonLabel,
  directVerifyPath,
  kindLabel,
  prerequisiteLabel,
  requestPath,
  watchlistReasonLabel,
} from '../../../../components/verification/labels';
import {
  EntityTypeBadge,
  RequestStatusBadge,
  StrengthBadge,
  VerificationErrorBanner,
} from '../../../../components/verification/VerificationBits';
import { verificationErrorCode } from '../../../../components/verification/verificationErrors';

type DialogKind = 'revoke' | 'liftCooldown' | 'unlockRerequest' | null;

interface LoadState {
  readonly key: string;
  readonly profile: AdminEntityVerificationProfile | null;
  readonly error: unknown;
}

function isEntityType(v: string | undefined): v is VerifiableEntityType {
  return v !== undefined && (VERIFIABLE_ENTITY_TYPES as readonly string[]).includes(v);
}

function RequestLink({ item }: { readonly item: AdminVerificationQueueItem }) {
  const { t } = useI18n();
  return (
    <Link
      to={requestPath(item.id)}
      className="flex flex-wrap items-center gap-2 rounded-md border border-hairline px-3 py-2 text-[13px] hover:bg-surface-2"
      data-testid={`entity-request-${item.id}`}
    >
      <span className="font-semibold text-ink">{kindLabel(t, item.requestKind)}</span>
      <RequestStatusBadge status={item.status} />
      <StrengthBadge score={item.evaluatorScore} riskFlags={item.riskFlags} />
      <span className="ms-auto text-accent">{t('admin:verification.common.openRequest')}</span>
    </Link>
  );
}

function Empty({ children }: { readonly children: ReactNode }) {
  return <p className="text-[13px] text-ink-3">{children}</p>;
}

export function VerificationEntityPage() {
  const params = useParams<{ type: string; id: string }>();
  const { t } = useI18n();
  const { formatDate } = useFormat();
  const navigate = useNavigate();
  const toast = useToast();
  const { canReview, canIssue } = useVerificationPermissions();
  const type = isEntityType(params.type) ? params.type : null;
  const id = params.id ?? '';

  const [reloadToken, setReloadToken] = useState(0);
  const [state, setState] = useState<LoadState>({ key: '', profile: null, error: null });
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [now] = useState(() => Date.now());
  const key = `${type ?? ''}:${id}#${reloadToken}`;

  useEffect(() => {
    if (!type) return undefined;
    let cancelled = false;
    const k = `${type}:${id}#${reloadToken}`;
    adminVerificationApi.entityProfile(type, id).then(
      (profile) => {
        if (!cancelled) setState({ key: k, profile, error: null });
      },
      (error: unknown) => {
        if (!cancelled) setState({ key: k, profile: null, error });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [type, id, reloadToken]);

  const reload = () => setReloadToken((n) => n + 1);
  const header = (
    <PageHeader
      title={t('admin:verification.pageTitle.entity')}
      actions={
        <Button size="sm" variant="ghost" icon="back" onClick={() => navigate('/verifications')}>
          {t('admin:verification.common.back')}
        </Button>
      }
    />
  );

  if (!type) {
    return (
      <div className="space-y-5">
        {header}
        <EmptyState title={t('admin:verification.entity.notFound')} />
      </div>
    );
  }

  if (state.key !== key) {
    return (
      <div className="space-y-5">
        {header}
        <div className="flex justify-center py-16">
          <Spinner size={32} />
        </div>
      </div>
    );
  }

  const p = state.profile;
  if (!p) {
    return (
      <div className="space-y-5">
        {header}
        {verificationErrorCode(state.error) === 'ENTITY_NOT_FOUND' ? (
          <EmptyState title={t('admin:verification.entity.notFound')} />
        ) : (
          <VerificationErrorBanner error={state.error} testId="entity-error" />
        )}
      </div>
    );
  }

  const verified = p.verificationStatus === 'verified';
  const cooldownActive = p.cooldownUntil !== null && new Date(p.cooldownUntil).getTime() > now;

  const done = (toastKey: string) => {
    setDialog(null);
    toast.push({ tone: 'success', title: t(toastKey) });
    reload();
  };

  return (
    <div className="space-y-5" data-testid="verification-entity-page">
      {header}

      <Card>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-[20px] font-extrabold text-ink" data-testid="entity-name">
                {p.entityName}
              </h2>
              {verified ? <VerifiedTick entityType={p.entityType} size="md" /> : null}
              <EntityTypeBadge type={p.entityType} />
              <VerificationBadge status={p.verificationState} size="sm" />
              {p.nameLocked ? (
                <Badge tone="neutral" size="sm">
                  {t('admin:verification.entity.nameLocked')}
                </Badge>
              ) : null}
            </div>
            {p.entityLegalName ? (
              <p className="text-[13px] text-ink-2">{p.entityLegalName}</p>
            ) : null}
            {p.subjectDisplayName ? (
              <p className="text-[13px] text-ink-3">
                {t('admin:verification.common.owner')}: {p.subjectDisplayName}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              {cooldownActive && p.cooldownUntil ? (
                <Badge tone="warning" size="sm" data-testid="entity-cooldown">
                  {t('admin:verification.entity.cooldownUntil', {
                    date: formatDate(new Date(p.cooldownUntil)),
                  })}
                </Badge>
              ) : null}
              {p.rerequestRequiresAdmin ? (
                <Badge tone="warning" size="sm" data-testid="entity-rerequest-gated">
                  {t('admin:verification.entity.rerequestGated')}
                </Badge>
              ) : null}
            </div>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="entity-actions">
            {canReview && verified ? (
              <Button
                variant="destructive"
                data-testid="entity-revoke"
                onClick={() => setDialog('revoke')}
              >
                {t('admin:verification.entity.revoke')}
              </Button>
            ) : null}
            {canReview && cooldownActive ? (
              <Button
                variant="secondary"
                data-testid="entity-lift-cooldown"
                onClick={() => setDialog('liftCooldown')}
              >
                {t('admin:verification.entity.liftCooldown')}
              </Button>
            ) : null}
            {canReview && p.rerequestRequiresAdmin ? (
              <Button
                variant="secondary"
                data-testid="entity-unlock-rerequest"
                onClick={() => setDialog('unlockRerequest')}
              >
                {t('admin:verification.entity.unlockRerequest')}
              </Button>
            ) : null}
            {canIssue && !verified ? (
              <Button
                variant="primary"
                icon="shield"
                data-testid="entity-direct-verify"
                onClick={() => navigate(directVerifyPath(p.entityType, p.entityId))}
              >
                {t('admin:verification.entity.directVerify')}
              </Button>
            ) : null}
          </div>
        </div>
        {verified ? (
          <div className="mt-4" data-testid="entity-current-tick">
            <VerifiedTick entityType={p.entityType} variant="hero" since={p.verifiedAt} />
          </div>
        ) : (
          <p className="mt-4 text-[13px] text-ink-3">
            {t('admin:verification.entity.notVerified')}
          </p>
        )}
      </Card>

      {p.unmetPrerequisites.length > 0 ? (
        <Banner tone="warning" title={t('admin:verification.entity.unmet')}>
          <ul className="list-disc ps-5">
            {p.unmetPrerequisites.map((c) => (
              <li key={c}>{prerequisiteLabel(t, c)}</li>
            ))}
          </ul>
        </Banner>
      ) : null}

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title={t('admin:verification.entity.liveRequest')} testId="entity-live-request">
          {p.liveRequest ? (
            <RequestLink item={p.liveRequest} />
          ) : (
            <Empty>{t('admin:verification.entity.noLiveRequest')}</Empty>
          )}
        </Panel>
        <Panel title={t('admin:verification.entity.openRequests')} testId="entity-open-requests">
          {p.openRequests.length === 0 ? (
            <Empty>{t('admin:verification.entity.noOpenRequests')}</Empty>
          ) : (
            <div className="space-y-2">
              {p.openRequests.map((r) => (
                <RequestLink key={r.id} item={r} />
              ))}
            </div>
          )}
        </Panel>
        <Panel title={t('admin:verification.entity.watchlist')} testId="entity-watchlist">
          {p.watchlist ? (
            <div className="flex flex-wrap gap-1.5">
              <Badge tone={p.watchlist.status === 'open' ? 'warning' : 'neutral'} size="sm">
                {t(`admin:verification.watchlist.status.${p.watchlist.status}`)}
              </Badge>
              {(p.watchlist.reasonCodes as readonly WatchlistReasonCode[]).map((code) => (
                <Badge key={code} tone="warning" size="sm">
                  {watchlistReasonLabel(t, code)}
                </Badge>
              ))}
            </div>
          ) : (
            <Empty>{t('admin:verification.entity.noWatchlist')}</Empty>
          )}
        </Panel>
        <Panel title={t('admin:verification.entity.candidate')} testId="entity-candidate">
          {p.candidate ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone="info" size="sm">
                  {t(`admin:verification.candidates.status.${p.candidate.status}`)}
                </Badge>
                <StrengthBadge score={p.candidate.score} riskFlags={p.candidate.riskFlags} />
              </div>
              <div className="flex flex-wrap gap-1.5">
                {(p.candidate.reasonCodes as readonly CandidateReasonCode[]).map((code) => (
                  <Badge key={code} tone="info" size="sm">
                    {candidateReasonLabel(t, code)}
                  </Badge>
                ))}
              </div>
            </div>
          ) : (
            <Empty>{t('admin:verification.entity.noCandidate')}</Empty>
          )}
        </Panel>
      </div>

      <Panel title={t('admin:verification.entity.history')} testId="entity-history">
        {p.requests.length === 0 ? (
          <Empty>{t('admin:verification.entity.noHistory')}</Empty>
        ) : (
          <RequestHistoryTable rows={p.requests} />
        )}
      </Panel>

      <Panel title={t('admin:verification.panels.events')} testId="entity-events">
        <EventsTimeline events={p.events} />
      </Panel>

      {dialog === 'revoke' ? (
        <RevokeDialog
          entityType={p.entityType}
          entityId={p.entityId}
          entityName={p.entityName}
          onClose={() => setDialog(null)}
          onDone={() => done('admin:verification.toast.revoked')}
        />
      ) : null}
      {dialog === 'liftCooldown' ? (
        <ReasonDialog
          title={t('admin:verification.liftCooldown.title')}
          description={t('admin:verification.liftCooldown.description')}
          confirmLabel={t('admin:verification.liftCooldown.confirm')}
          testId="lift-cooldown-dialog"
          onSubmit={(reason) =>
            adminVerificationApi.liftCooldown(p.entityType, p.entityId, { reason })
          }
          onClose={() => setDialog(null)}
          onDone={() => done('admin:verification.toast.cooldownLifted')}
        />
      ) : null}
      {dialog === 'unlockRerequest' ? (
        <ReasonDialog
          title={t('admin:verification.unlockRerequest.title')}
          description={t('admin:verification.unlockRerequest.description')}
          confirmLabel={t('admin:verification.unlockRerequest.confirm')}
          testId="unlock-rerequest-dialog"
          onSubmit={(reason) =>
            adminVerificationApi.unlockRerequest(p.entityType, p.entityId, { reason })
          }
          onClose={() => setDialog(null)}
          onDone={() => done('admin:verification.toast.rerequestUnlocked')}
        />
      ) : null}
    </div>
  );
}
